import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import PropTypes from "prop-types";
import moment from "moment";

const Chart = ({ value, iosData, androidData }) => {
  // Helper function to group data by date
  const groupDataByDate = (data, daysCount) => {
    const today = moment().startOf("day");
    const startDay = moment().subtract(daysCount - 1, "days").startOf("day");

    const dailyDataArray = [];
    for (let i = 0; i < daysCount; i++) {
      const currentDay = moment(startDay).add(i, "days").format("YYYY-MM-DD");
      dailyDataArray.push({ date: currentDay, data: [], count: 0 });
    }

    data.forEach((entry) => {
      const entryDate = moment(entry.dateTime).format("YYYY-MM-DD");
      const dayObject = dailyDataArray.find((day) => day.date === entryDate);
      if (dayObject) {
        // Check if this is Android API data (has count property) or iOS database data
        if (entry.count !== undefined) {
          // This is Android data from API with count property
          dayObject.count += entry.count;
        } else {
          // This is iOS data - each entry is a download
          dayObject.data.push(entry);
          dayObject.count = dayObject.data.length;
        }
      }
    });

    return dailyDataArray;
  };

  const getOptions = () => {
    let iosDownloadData = [];
    let androidDownloadData = [];
    let categories = [];

    const today = moment();

    switch (value) {
      case "Last 7 days": {
        const iosGrouped = groupDataByDate(iosData, 7);
        const androidGrouped = groupDataByDate(androidData, 7);

        iosDownloadData = iosGrouped.map((d) => d.count);
        androidDownloadData = androidGrouped.map((d) => d.count);
        categories = iosGrouped.map((d) => moment(d.date).format("ddd, MMM D"));
        break;
      }

      case "Last 15 days": {
        const iosGrouped = groupDataByDate(iosData, 15);
        const androidGrouped = groupDataByDate(androidData, 15);

        iosDownloadData = iosGrouped.map((d) => d.count);
        androidDownloadData = androidGrouped.map((d) => d.count);
        categories = iosGrouped.map((d) => moment(d.date).format("MMM D"));
        break;
      }

      case "Last 30 days": {
        const iosGrouped = groupDataByDate(iosData, 30);
        const androidGrouped = groupDataByDate(androidData, 30);

        iosDownloadData = iosGrouped.map((d) => d.count);
        androidDownloadData = androidGrouped.map((d) => d.count);
        categories = iosGrouped.map((d) => moment(d.date).format("MMM D"));
        break;
      }

      case "Over All": {
        if (iosData.length === 0 && androidData.length === 0) {
          // No data at all
          iosDownloadData = [0];
          androidDownloadData = [0];
          categories = [moment().format("MMM D")];
        } else {
          // Create a map to store data by date
          const dateDataMap = new Map();

          // Process iOS data
          iosData.forEach((entry) => {
            const date = moment(entry.dateTime).format("YYYY-MM-DD");
            if (!dateDataMap.has(date)) {
              dateDataMap.set(date, { iosCount: 0, androidCount: 0 });
            }
            dateDataMap.get(date).iosCount++;
          });

          // Process Android data
          androidData.forEach((entry) => {
            const date = moment(entry.dateTime).format("YYYY-MM-DD");
            if (!dateDataMap.has(date)) {
              dateDataMap.set(date, { iosCount: 0, androidCount: 0 });
            }
            // Android data from API has count property
            if (entry.count !== undefined) {
              dateDataMap.get(date).androidCount += entry.count;
            } else {
              dateDataMap.get(date).androidCount++;
            }
          });

          // Sort dates and create arrays
          const sortedDates = Array.from(dateDataMap.keys()).sort();

          categories = sortedDates.map(date => moment(date).format("MMM D"));
          iosDownloadData = sortedDates.map(date => dateDataMap.get(date).iosCount);
          androidDownloadData = sortedDates.map(date => dateDataMap.get(date).androidCount);
        }
        break;
      }

      default:
        // Fallback for any unknown value
        const iosGrouped = groupDataByDate(iosData, 7);
        const androidGrouped = groupDataByDate(androidData, 7);

        iosDownloadData = iosGrouped.map((d) => d.count);
        androidDownloadData = androidGrouped.map((d) => d.count);
        categories = iosGrouped.map((d) => moment(d.date).format("ddd, MMM D"));
        break;
    }

    return {
      chart: {
        type: "line",
        backgroundColor: "#fff",
        borderRadius: 10,
        width: null,
        height: 240,
        style: {
          fontFamily: "'Arial', sans-serif",
        },
      },
      title: {
        text: null,
      },
      xAxis: {
        categories,

        title: {
          text: null,
        },
        labels: {
          style: {
            color: "#474747",
            fontSize: "10px",
          },
        },
        gridLineDashStyle: "dot", // Dotted lines for Y-axis gridlines
        gridLineWidth: 2, // Ensures Y-axis gridline visibility
      },
      yAxis: {
        title: {
          text: null,
        },
        labels: {
          style: {
            color: "#474747",
            fontSize: "12px",
          },
        },
        gridLineWidth: 0, // Removes gridlines for X-axis
      },
      series: [
        {
          name: "Apple Store",
          data: iosDownloadData,
          type: "line",
          color: "#17DEEB",
          lineWidth: 1.2,
          marker: {
            enabled: false,
          },
        },
        {
          name: "Play Store",
          data: androidDownloadData,
          type: "line",
          color: "#4951EC",
          lineWidth: 1.2,
          marker: {
            enabled: false,
          },
        },
      ],
      legend: {
        enabled: false, // Disables the legend
      },
      credits: {
        enabled: false,
      },
    };
  };

  return (
    <div className="h-50">
      <HighchartsReact highcharts={Highcharts} options={getOptions()} />
    </div>
  );
};

Chart.propTypes = {
  value: PropTypes.string.isRequired,
  iosData: PropTypes.array.isRequired,
  androidData: PropTypes.array.isRequired,
};

export default Chart;
